import { useEffect, useReducer } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSetting, updateSettings } from "../../../redux/courses.ts";
import { useParams } from "react-router-dom";
import { Loader } from "../../../components/loader/index.js";
import { UsersList } from "../../../components/usersList.js/index.js";
import { RxCross1 } from "react-icons/rx";
import { Combobox, ComboboxButton, Transition } from "@headlessui/react";
import { IoChevronDown } from "react-icons/io5";
import { debounce } from "lodash";
import { fetchUserList } from "../../../redux/courses.ts";
// State reducer to manage complex state
function settingsReducer(state, action) {
  switch (action.type) {
    case "SET_SETTINGS":
      return { ...state, ...action.payload };
    case "TOGGLE_SETTING":
      return {
        ...state,
        settings: {
          ...state.settings,
          [action.payload]: !state.settings[action.payload],
        },
      };
    case "ADD_USER":
      return {
        ...state,
        userList: [...state.userList, action.payload],
      };
    case "REMOVE_USER":
      return {
        ...state,
        userList: state.userList.filter((u) => u.id !== action.payload),
      };
    case "ADD_EMAIL":
      return {
        ...state,
        inviteEmails: [...state.inviteEmails, action.payload],
        currentEmail: "",
      };
    case "REMOVE_EMAIL":
      return {
        ...state,
        inviteEmails: state.inviteEmails.filter(
          (email, i) => i !== action.payload
        ),
      };
    case "SET_CURRENT_EMAIL":
      return {
        ...state,
        currentEmail: action.payload,
      };
    case "CLEAR_EMAILS":
      return {
        ...state,
        inviteEmails: [],
      };
    default:
      return state;
  }
}

export const Settings = () => {
  const { title, type } = useParams();
  const dispatch = useDispatch();
  const { settings, isLoading, users } = useSelector((store) => store.course);

  const [state, dispatchState] = useReducer(settingsReducer, {
    settings: {
      "Manager can enroll": false,
      "Course Rating": false,
      "Email on review": false,
      "Disable instructor message": false,
      "Allow Unenroll Course": false,
      Algorithm: false,
    },
    userList: [],
    inviteEmails: [],
    currentEmail: "",
    courseURL: "",
    invitedEmails: [],
  });

  useEffect(() => {
    dispatch(fetchSetting(title));
  }, [dispatch, title]);

  const dispatchFetchUsersList = debounce((value) => {
    dispatch(fetchUserList({ user: value, page: 1 }));
  }, 500);

  const handleScroll = (e) => {
    const target = e.target;
    if (
      target.scrollHeight - target.scrollTop === target.clientHeight &&
      users?.current_page < users?.last_page
    ) {
      dispatch(
        fetchUserList({
          user: state.currentEmail,
          page: users?.current_page + 1,
        })
      );
    }
  };

useEffect(() => {
  if (settings) {
    dispatchState({
      type: "SET_SETTINGS",
      payload: {
        settings: {
          "Manager can enroll":
            settings?.course?.course_setting?.manager_can_enroll ?? false,
          "Course Rating":
            settings?.course?.course_setting?.course_rating ?? false,
          "Email on review":
            settings?.course?.course_setting?.email_on_review ?? false,
          "Disable instructor message":
            settings?.course?.course_setting?.disable_instructor_message ?? false,
          "Allow Unenroll Course":
            settings?.course?.course_setting?.allow_unenroll_course ?? false,
          Algorithm: settings?.course?.course_setting?.algorithm ?? false,
        },
        userList: settings?.course?.users ?? [],
        courseURL: settings?.course_url ?? "",
        invitedEmails: settings?.invited_emails ?? [],
      },
    });
  }
}, [settings]);

const handleUpdate = () => {
  if (state.userList.length === 0) return;

  const updatedSettings = {
    slug: title,
    manager_can_enroll: Boolean(state.settings["Manager can enroll"]),
    course_rating: Boolean(state.settings["Course Rating"]),
    email_on_review: Boolean(state.settings["Email on review"]),
    disable_instructor_message: Boolean(
      state.settings["Disable instructor message"]
    ),
    allow_unenroll_course: Boolean(state.settings["Allow Unenroll Course"]),
    algorithm: Boolean(state.settings["Algorithm"]),
    users: state.userList.map((user) => user?.id),
    invited_emails: [...state.invitedEmails, ...state.inviteEmails], // Or use course_interests
  };

  dispatch(updateSettings(updatedSettings));
  dispatchState({ type: "CLEAR_EMAILS" });
};

  const handleKeyDown = (e) => {
    if (["Enter", "Tab", ","].includes(e.key) && state.currentEmail.trim()) {
      e.preventDefault();
      const email = state.currentEmail.trim();
      if (validateEmail(email)) {
        dispatchState({ type: "ADD_EMAIL", payload: email });
      }
    }
  };

  const validateEmail = (email) => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  if (isLoading?.settings) {
    return (
      <div className="flex justify-center py-10">
        <Loader w="20" h="20" color="orange" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 bg-white border border-gray-300 rounded shadow">
      <h2 className="text-xl font-semibold border-b pb-2">Course Settings</h2>

      {/* Course URL */}
      <div className="word-break">
        <span className="font-medium">Course URL: </span>
        <span
          onClick={async (e) => {
            if (!state.courseURL) return;
            try {
              await navigator.clipboard.writeText(state.courseURL);
              const originalText = e.currentTarget.textContent;
              e.currentTarget.textContent = "Copied!";
              setTimeout(() => {
                e.currentTarget.textContent = originalText;
              }, 2000);
            } catch (err) {
              console.error("Failed to copy: ", err);
              const textArea = document.createElement("textarea");
              textArea.value = state.courseURL;
              document.body.appendChild(textArea);
              textArea.select();
              try {
                document.execCommand("copy");
              } catch (err) {
                console.error("Fallback copy failed: ", err);
              }
              document.body.removeChild(textArea);
            }
          }}
          className="text-blue-600 underline cursor-pointer"
        >
          {state.courseURL}
        </span>
        <span className="ml-2 text-sm text-gray-500">(click to copy)</span>
      </div>

      {/* Settings Toggles */}
      {/* <div className="grid md:grid-cols-2 gap-4">
        {Object.keys(state.settings).map((set, index) => (
          <div
            key={index}
            className="flex justify-between items-center border p-3 rounded"
          >
            <span className="capitalize">{set}</span>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={!!state.settings[set]}
                onChange={() => dispatchState({ type: "TOGGLE_SETTING", payload: set })}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 rounded-full peer peer-checked:bg-orange-600 peer-focus:outline-none after:content-[''] after:absolute after:w-5 after:h-5 after:bg-white after:rounded-full after:transition-all after:top-[2px] after:left-[2px] peer-checked:after:translate-x-full" />
            </label>
          </div>
        ))}
      </div> */}

      {/* Invite Section */}
      <div>
        <h3 className="text-lg font-semibold mb-2">
          Invite only registered users
        </h3>
        <div className="flex flex-wrap gap-2 mb-2 border border-gray-300 p-2 rounded min-h-12">
          {state.inviteEmails.map((email, index) => (
            <div
              key={index}
              className="flex items-center gap-2 bg-gray-100 px-2 py-1 rounded-full text-sm"
            >
              {email}
              <button
                onClick={() =>
                  dispatchState({ type: "REMOVE_EMAIL", payload: index })
                }
                className="text-red-600"
              >
                <RxCross1 size={12} />
              </button>
            </div>
          ))}

          <Combobox
            onChange={(selectedUser) => {
              if (selectedUser?.email) {
                dispatchState({
                  type: "ADD_EMAIL",
                  payload: selectedUser.email,
                });
                // Clear the input after selection
                dispatchState({ type: "SET_CURRENT_EMAIL", payload: "" });
              }
            }}
          >
            <div className="relative flex-1 min-w-[200px]">
              <div className="flex justify-center">
                <Combobox.Input
                  autoComplete="off"
                  placeholder="Search users by name or email"
                  value={state.currentEmail}
                  onChange={(e) => {
                    dispatchState({
                      type: "SET_CURRENT_EMAIL",
                      payload: e.target.value,
                    });
                    dispatchFetchUsersList(e.target.value);
                  }}
                  className="bg-transparent outline-none w-full pr-10"
                />
                <ComboboxButton className="group absolute inset-y-0 right-0 px-2.5">
                  <IoChevronDown className="size-4 fill-gray-600 group-data-[hover]:fill-gray-800" />
                </ComboboxButton>
              </div>
              <Transition
                leave="transition ease-in duration-100"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
              >
                <Combobox.Options
                  onScroll={handleScroll}
                  className="absolute mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black/5 focus:outline-none sm:text-sm z-10"
                >
                  {(users?.data || [])?.length === 0 && (
                    <div className="relative cursor-default select-none py-2 px-4 text-gray-700">
                      {isLoading?.users ? "Searching..." : "No users found"}
                    </div>
                  )}
                  {(users?.data || [])?.map((user) => (
                    <Combobox.Option
                      key={user.id}
                      value={user}
                      className={({ active }) =>
                        `relative cursor-default select-none py-2 px-4 ${
                          active ? "bg-orange-600 text-white" : "text-gray-900"
                        }`
                      }
                    >
                      {({ selected }) => (
                        <div className="flex flex-col">
                          <span className="block truncate">
                            {user.firstname} {user.lastname}
                          </span>
                          <span className="block truncate text-xs text-gray-500">
                            {user.email}
                          </span>
                        </div>
                      )}
                    </Combobox.Option>
                  ))}
                </Combobox.Options>
              </Transition>
            </div>
          </Combobox>
        </div>
      </div>

      <div>
        <h3 className="text-lg font-semibold mb-2">Invited Emails</h3>
        {state.invitedEmails.length > 0 ? (
          <div className="flex flex-wrap gap-2">
            {state.invitedEmails.map((email, index) => (
              <div
                key={index}
                className="flex items-center gap-2 bg-gray-100 px-3 py-1 rounded-full text-sm"
              >
                {email}
                {/* <button
                  onClick={() => {
                    // Dispatch API call to remove email from course_interests
                    dispatch(
                      updateSettings({
                        slug: title,
                        invited_emails: state.invitedEmails.filter(
                          (_, i) => i !== index
                        ),
                      })
                    );
                    // Update local state
                    dispatchState({
                      type: "SET_SETTINGS",
                      payload: {
                        ...state,
                        invitedEmails: state.invitedEmails.filter(
                          (_, i) => i !== index
                        ),
                      },
                    });
                  }}
                  className="text-red-600"
                >
                  <RxCross1 size={12} />
                </button> */}
              </div>
            ))}
          </div>
        ) : (
          <p className="text-gray-500">No emails have been invited yet</p>
        )}
      </div>

      {/* Teams Users */}
      <div>
        <h3 className="text-lg font-semibold mb-2">Trainers</h3>
        <UsersList
          setUserInfo={(userInfo) => {
            if (userInfo?.userId) {
              dispatchState({
                type: "ADD_USER",
                payload: { id: userInfo.userId, userName: userInfo.userName },
              });
            }
          }}
          userList={state.userList}
        />
        {state.userList.length === 0 && (
          <p className="text-red-600 text-sm mt-2">
            At least one user is required!
          </p>
        )}
        <div className="flex flex-wrap gap-2 mt-4">
          {state.userList.map((user, idx) => (
            <div
              key={idx}
              className="flex items-center gap-2 border px-3 py-1 rounded-full shadow-sm bg-gray-50 text-sm"
            >
              {user?.userName ?? `${user?.firstname} ${user?.lastname}`}
              <button
                onClick={() =>
                  dispatchState({ type: "REMOVE_USER", payload: user.id })
                }
                className="text-red-600"
              >
                <RxCross1 size={12} />
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Update Button */}
      <div className="pt-4">
        <button
          onClick={handleUpdate}
          disabled={state.userList.length === 0}
          className="px-5 py-2 border-2 border-orange-600 text-orange-600 rounded hover:bg-orange-600 hover:text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isLoading?.settingUpdate ? (
            <Loader w="8" h="8" color="orange" />
          ) : (
            "Update Settings"
          )}
        </button>
      </div>
    </div>
  );
};
